import {
  B2B_PORTFOLIO_OWNER_TYPE,
  OwnerTypeTag,
  TREASURY_MONTHS,
  TREASURY_NOT_SET_UP,
  TREASURY_PORTFOLIO_OWNER_TYPE,
  TREASURY_REDEEM_FREQUENCIES,
  TreasuryStep,
} from '../constants/treasury';
import { TreasuryCompanySetup, TreasuryCompanyStatus, TreasuryKyb, TreasuryRedeemAutomation } from '../types/treasury';

/** Treasury for the company's treasury portfolio, B2B for an employee's, B2C for anyone else (or no owner type). */
export const getOwnerTypeTag = (ownerTypeName?: string | null): OwnerTypeTag => {
  if (ownerTypeName === TREASURY_PORTFOLIO_OWNER_TYPE) {
    return OwnerTypeTag.TREASURY;
  }

  return ownerTypeName === B2B_PORTFOLIO_OWNER_TYPE ? OwnerTypeTag.B2B : OwnerTypeTag.B2C;
};

/** A redemption rule's schedule in words, e.g. "Yearly — day 10 March". */
export const getTreasuryRedeemScheduleLabel = (rule: TreasuryRedeemAutomation): string => {
  const frequency = TREASURY_REDEEM_FREQUENCIES.find((option) => option.value === rule.frequency)?.label;
  const month = rule.monthOfYear ? ` ${TREASURY_MONTHS[rule.monthOfYear - 1]?.label}` : '';

  return `${frequency ?? rule.frequency} — day ${rule.dayOfMonth}${month}`;
};

/**
 * A picked date as YYYY-MM-DD in the browser's own calendar. Not toISOString(): the picker gives local midnight, and in
 * Riyadh (UTC+3) its UTC form is the previous day.
 */
export const toDateOnly = (date: Date | null): string => {
  if (!date || Number.isNaN(date.getTime())) {
    return '';
  }

  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${date.getFullYear()}-${month}-${day}`;
};

/**
 * The message of a failed treasury call. RTK Query puts the backend's body on `error.data` (`{ code, message }`, plus
 * class-validator `errors` on a 400 — joined here, as the axios client did); a network failure has only `error.error`.
 */
export const getTreasuryErrorMessage = (error: any, fallback: string): string => {
  const body = error?.data ?? error;

  if (Array.isArray(body?.errors)) {
    const messages = body.errors.flatMap((item: any) => Object.values(item?.constraints ?? {}));

    if (messages.length) {
      return messages.join('\n');
    }
  }

  return body?.message || error?.error || fallback;
};

/** Whether a failed setup fetch means "this company has no treasury yet" (404 / TREASURY_NOT_SET_UP). */
export const isTreasuryNotSetUpError = (error: any): boolean => {
  const body = error?.data ?? error;

  return error?.status === 404 || body?.code === 404 || String(body?.message ?? '').includes(TREASURY_NOT_SET_UP);
};

/**
 * The step the wizard is on, worked out from the backend's answers — nothing is kept in the browser.
 * The steps after the treasury employee need their own reads, so they come in with those screens.
 */
export const getTreasuryStep = (
  setup: TreasuryCompanySetup | null,
  kyb: TreasuryKyb | null = null,
  isKycConfirmed = false,
): TreasuryStep => {
  if (!setup) {
    return TreasuryStep.GroupAndDepartment;
  }

  if (setup.employeeId == null) {
    return TreasuryStep.TreasuryEmployee;
  }

  // Approving the employee makes the setup ACTIVE. Before that it is the personal email step — the backend keeps no
  // record of it (it only replaces a still-work email), so the page moves to Approve once it is saved.
  if (setup.status !== TreasuryCompanyStatus.Active) {
    return TreasuryStep.PersonalEmail;
  }

  // Approve copies the KYC but leaves its confirm date empty; the owner can't turn Active without it, so this comes
  // back first even for a treasury already further on.
  if (!isKycConfirmed) {
    return TreasuryStep.ConfirmKyc;
  }

  // The KYB carries the next three: saved → verified by a Madkhol admin → Alpaca agreements accepted.
  if (!kyb) {
    return TreasuryStep.CompanyKyb;
  }

  if (!kyb.verification) {
    return TreasuryStep.AdminVerification;
  }

  if (!kyb.agreements && !setup.agreements) {
    return TreasuryStep.AlpacaAgreements;
  }

  return TreasuryStep.AlpacaAccount;
};
